// Story 4.3 — PR status list (AC3). PR state is computed live by the backend
// (open → pending, merged+synced → applied vX, closed → closed), so this is a
// thin render layer over GET /policy-changes.
// Story 4.4 — pending items gain Approve / Request-changes actions (Path B:
// approve = direct merge, request-changes = PR comment).
import { useState } from 'react';
import { Alert, App, Button, Input, List, Modal, Popconfirm, Space, Tag, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BidiText, MonoCode, formatDateBidi } from '@lebinatikra/rtl-utils';
import { FilterApiError } from '../../lib/filter-api-client';
import { policyApi, type PolicyChangeItem } from '../../lib/policy-api';

export function prChangesQueryKey(communityId: string): readonly unknown[] {
  return ['policy-changes', communityId];
}

const STATUS_COLOR: Record<PolicyChangeItem['status'], string> = {
  pending: 'gold',
  merging: 'blue',
  applied: 'green',
  closed: 'default',
};

export function PrStatusList({ communityId }: { communityId: string }) {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const [rcTarget, setRcTarget] = useState<number | null>(null);
  const [rcComment, setRcComment] = useState('');

  const query = useQuery({
    queryKey: prChangesQueryKey(communityId),
    queryFn: () => policyApi.listChanges(communityId),
    enabled: Boolean(communityId),
    refetchOnWindowFocus: false,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: prChangesQueryKey(communityId) });

  const errorMessage = (err: unknown): string =>
    err instanceof FilterApiError ? err.message : t('policy_editor.approve_error');

  const approve = useMutation({
    mutationFn: (prNumber: number) => policyApi.approve(communityId, prNumber),
    onSuccess: () => {
      notification.success({ message: t('policy_editor.approve_success') });
      void invalidate();
    },
    onError: (err) =>
      notification.error({
        message: t('policy_editor.approve_error'),
        description: errorMessage(err),
      }),
  });

  const requestChanges = useMutation({
    mutationFn: ({ prNumber, comment }: { prNumber: number; comment: string }) =>
      policyApi.requestChanges(communityId, prNumber, comment),
    onSuccess: () => {
      notification.success({ message: t('policy_editor.request_changes_success') });
      setRcTarget(null);
      setRcComment('');
      void invalidate();
    },
    onError: (err) =>
      notification.error({
        message: t('policy_editor.request_changes_error'),
        description: errorMessage(err),
      }),
  });

  if (query.error) {
    return (
      <Alert
        type="error"
        message={(query.error as Error).message}
        data-testid="pr-list-error"
      />
    );
  }

  const statusLabel = (item: PolicyChangeItem): string => {
    if (item.status === 'applied') {
      return t('policy_editor.status.applied', { version: item.applied_version ?? '?' });
    }
    return t(`policy_editor.status.${item.status}`);
  };

  // Only a live (pending) PR can be approved or have changes requested;
  // applied / closed items have nothing to act on.
  const itemActions = (item: PolicyChangeItem) => {
    const actions = [
      <a key="pr" href={item.pr_url} target="_blank" rel="noreferrer">
        <MonoCode>{`#${item.pr_number}`}</MonoCode>
      </a>,
    ];
    if (item.status === 'pending') {
      actions.push(
        <Popconfirm
          key="approve"
          title={t('policy_editor.approve_confirm')}
          onConfirm={() => approve.mutate(item.pr_number)}
          okText={t('policy_editor.approve')}
          cancelText={t('common.cancel')}
        >
          <Button
            type="primary"
            size="small"
            loading={approve.isPending && approve.variables === item.pr_number}
            data-testid={`approve-${item.pr_number}`}
          >
            {t('policy_editor.approve')}
          </Button>
        </Popconfirm>,
        <Button
          key="request-changes"
          size="small"
          onClick={() => {
            setRcComment('');
            setRcTarget(item.pr_number);
          }}
          data-testid={`request-changes-${item.pr_number}`}
        >
          {t('policy_editor.request_changes')}
        </Button>,
      );
    }
    return actions;
  };

  return (
    <>
      <List
        loading={query.isLoading}
        dataSource={query.data ?? []}
        locale={{ emptyText: t('policy_editor.no_changes') }}
        data-testid="pr-status-list"
        renderItem={(item) => (
          <List.Item actions={itemActions(item)}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Tag color={STATUS_COLOR[item.status]} data-testid={`pr-status-${item.pr_number}`}>
                  {statusLabel(item)}
                </Tag>
                <Typography.Text type="secondary">
                  {formatDateBidi(item.created_at)}
                </Typography.Text>
              </Space>
              <BidiText>{item.diff_summary}</BidiText>
            </Space>
          </List.Item>
        )}
      />

      <Modal
        open={rcTarget !== null}
        title={t('policy_editor.request_changes_title')}
        okText={t('policy_editor.request_changes')}
        cancelText={t('common.cancel')}
        confirmLoading={requestChanges.isPending}
        okButtonProps={{ disabled: rcComment.trim().length === 0 }}
        onOk={() => {
          if (rcTarget !== null && rcComment.trim().length > 0) {
            requestChanges.mutate({ prNumber: rcTarget, comment: rcComment.trim() });
          }
        }}
        onCancel={() => setRcTarget(null)}
        data-testid="request-changes-modal"
      >
        <Typography.Paragraph>
          {t('policy_editor.request_changes_comment_label')}
        </Typography.Paragraph>
        <Input.TextArea
          rows={4}
          value={rcComment}
          onChange={(e) => setRcComment(e.target.value)}
          placeholder={t('policy_editor.request_changes_placeholder')}
          maxLength={4000}
          data-testid="request-changes-comment"
        />
      </Modal>
    </>
  );
}
